import React, { useState, useEffect } from "react";
import { useMenuAccess } from "../contexts/MenuAccessContext";
import toast from "react-hot-toast";

const MenuAccessManagement = () => {
  const { menuAccess, updateMenuAccess, fetchMenuAccess } = useMenuAccess();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});

  const roles = ["sales", "orders", "telecaller"];
  const menuItems = [
    { key: "leads", label: "Leads Management", icon: "👥" },
    { key: "appointments", label: "Appointments", icon: "📅" },
    { key: "orders", label: "Orders", icon: "🛒" },
    { key: "tasks", label: "Tasks", icon: "✅" },
    { key: "analytics", label: "Analytics", icon: "📊" },
    { key: "communications", label: "Communications", icon: "💬" },
    { key: "calls", label: "Call Management", icon: "📞" },
  ];

  useEffect(() => {
    fetchMenuAccess();
  }, []);

  const handleAccessChange = async (role, menu, hasAccess) => {
    const savingKey = `${role}-${menu}`;
    setSaving((prev) => ({ ...prev, [savingKey]: true }));

    try {
      await updateMenuAccess(role, menu, hasAccess);
      toast.success(
        `Access ${hasAccess ? "granted" : "revoked"} for ${role} - ${menu}`
      );
    } catch (error) {
      toast.error(`Failed to update access for ${role} - ${menu}`);
      console.error("Error updating menu access:", error);
    } finally {
      setSaving((prev) => ({ ...prev, [savingKey]: false }));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Menu Access Management
      </h2>
      <p className="text-gray-600 mb-6">
        Control which dashboard features each role can access. Changes are
        applied immediately.
      </p>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Menu Item
              </th>
              {roles.map((role) => (
                <th
                  key={role}
                  className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {role.charAt(0).toUpperCase() + role.slice(1)} Role
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {menuItems.map((item) => (
              <tr key={item.key} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className="text-lg mr-3">{item.icon}</span>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {item.label}
                      </div>
                      <div className="text-sm text-gray-500">{item.key}</div>
                    </div>
                  </div>
                </td>
                {roles.map((role) => {
                  const hasAccess = menuAccess[role]?.[item.key] || false;
                  const savingKey = `${role}-${item.key}`;
                  const isSaving = saving[savingKey];

                  return (
                    <td
                      key={`${role}-${item.key}`}
                      className="px-6 py-4 text-center"
                    >
                      <div className="relative inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={hasAccess}
                          onChange={(e) =>
                            handleAccessChange(role, item.key, e.target.checked)
                          }
                          disabled={isSaving}
                          className="sr-only peer"
                        />
                        <div
                          className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out cursor-pointer ${
                            hasAccess ? "bg-blue-600" : "bg-gray-200"
                          } ${isSaving ? "opacity-50 cursor-not-allowed" : ""}`}
                          onClick={() =>
                            !isSaving &&
                            handleAccessChange(role, item.key, !hasAccess)
                          }
                        >
                          <div
                            className={`absolute top-[2px] left-[2px] bg-white border border-gray-300 rounded-full h-5 w-5 transition-transform duration-200 ease-in-out ${
                              hasAccess ? "translate-x-5" : "translate-x-0"
                            }`}
                          />
                        </div>
                        {isSaving && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-4 h-4">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-sm font-medium text-blue-900 mb-2">
          Access Notes:
        </h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>
            • <strong>Sales Role:</strong> Can manage leads, appointments, and
            view analytics
          </li>
          <li>
            • <strong>Orders Role:</strong> Can manage orders and view order
            analytics
          </li>
          <li>
            • <strong>Telecaller Role:</strong> Can access leads for calling and
            manage call tasks
          </li>
          <li>
            • <strong>Admin Role:</strong> Always has full access to all
            features
          </li>
        </ul>
      </div>

      <div className="mt-4 flex justify-between items-center">
        <button
          onClick={fetchMenuAccess}
          disabled={loading}
          className="btn-secondary btn-md flex items-center gap-2"
        >
          <span className={loading ? "animate-spin" : ""}>🔄</span>
          {loading ? "Refreshing..." : "Refresh Settings"}
        </button>

        <div className="text-sm text-gray-500">
          Last updated: {new Date().toLocaleString()}
        </div>
      </div>
    </div>
  );
};

export default MenuAccessManagement;
